import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleX, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import { currentSession } from '../../auth/passwordSession';
import { AiBadge } from './AiBadge';
import { type ActionsBase, useCancelRun, useImageActions, useLiveImageRuns, useStartRun } from './api';
import { outputSections, type OutputEnv } from './outputs/registry';
import { RunDialog } from './RunDialog';
import {
  isActive,
  isEnabled,
  permittedUseLabel,
  provenanceLine,
  runState,
  runsOnDemand,
  sectionsByCategory,
  stateLabel,
} from './states';
import type { Action, Run, StartRunRequest } from './types';
import type { ImageKind } from '../../data/imageTypes';

export interface AiPanelProps {
  /** The slide's engine while the image is open in the single view; null otherwise. */
  engine: ViewportEngine | null;
  /** Why there is no engine, in words, when there is none (compare). */
  noEngineReason?: string;
  /** The open image's public id (`img_…`); undefined for a series the navigator does not know. */
  imageId: string | undefined;
  imageKind?: ImageKind;
  studyUID: string | undefined;
  seriesUID: string | undefined;
  /** May start and cancel runs: an editor or administrator of the image's workspace. Never through a share link. */
  canRun: boolean;
  /** Where the panel reads: the app's own API (''), or a share link's namespace (shareBase). */
  base?: ActionsBase;
}

/** One action as the panel shows it: the catalogue's entry, or only what a run of it says. */
interface Entry {
  id: string;
  name: string;
  category: string | null;
  ai: boolean;
  action: Action | null;
  latest: Run | null;
}

/**
 * Whether an action may be started on an image of this kind: a slide, or one
 * the navigator does not know (undefined). Never a flat picture or a document
 * (the server refuses both, actions.ts planViewerRun).
 */
const runsOnKind = (kind: ImageKind | undefined): boolean => kind !== 'flat' && kind !== 'document';

/** The actions to show for this image, by section, each with its latest run. */
export function panelEntries(actions: Action[], runs: Run[], imageKind: ImageKind | undefined): Array<{ title: string; entries: Entry[] }> {
  const byAction = new Map<string, Run>();
  // Runs come newest first: the first of each action is its latest.
  for (const r of runs) if (runState(r) && !byAction.has(r.action.id)) byAction.set(r.action.id, r);
  const entries: Entry[] = [];
  for (const m of actions) {
    const latest = byAction.get(m.id) ?? null;
    const usable = isEnabled(m) && runsOnDemand(m) && runsOnKind(imageKind);
    if (!latest && !usable) continue;
    entries.push({ id: m.id, name: m.name, category: m.category ?? null, ai: m.ai === true, action: m, latest });
  }
  // An action that ran here but is no longer offered keeps its results.
  for (const [id, r] of byAction) {
    if (actions.some((m) => m.id === id)) continue;
    entries.push({ id, name: r.action.name, category: r.action.category ?? null, ai: r.action.ai === true, action: null, latest: r });
  }
  return sectionsByCategory(entries, (e) => e.category, (e) => e.name);
}

/**
 * The AI tab: what actions found on the open image, by section (Quality
 * control, then Analysis), each action's latest run in one of four states, its
 * results in reading order — flag, measurements, masks — and who ran it,
 * when, with which version and for which use. Runs update live; nothing here
 * goes to the notification bell.
 */
export function AiPanel({ engine, noEngineReason, imageId, imageKind, studyUID, seriesUID, canRun, base = '' }: AiPanelProps) {
  const actions = useImageActions(imageId, base);
  const runs = useLiveImageRuns(imageId, base);
  const sections = useMemo(
    () => panelEntries(actions.data ?? [], runs.data ?? [], imageKind),
    [actions.data, runs.data, imageKind],
  );
  const env: OutputEnv = { engine, studyUID, noLayers: noEngineReason };
  // Who "you" is, for "Run by you": a run names its person by user id only.
  const me = useQuery({ queryKey: ['auth', 'session'], queryFn: currentSession, staleTime: Infinity, retry: false }).data?.userId ?? null;

  if (!imageId) {
    return <Empty>AI results are kept for the images in this study’s list.</Empty>;
  }
  if (actions.isLoading || runs.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const failure = (runs.error ?? actions.error) as Error | null;
  if (sections.length === 0) {
    return <Empty>{failure ? failure.message : 'No actions are available for this image.'}</Empty>;
  }
  return (
    <div className="flex flex-col gap-4 p-3" data-series={seriesUID}>
      {failure ? (
        <p role="alert" className="text-xs text-destructive">
          {failure.message}
        </p>
      ) : null}
      {sections.map((s) => (
        <section key={s.title} className="flex flex-col gap-2">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.title}</h2>
          {s.entries.map((e) => (
            <ActionResult key={e.id} entry={e} imageId={imageId} imageKind={imageKind} canRun={canRun} env={env} me={me} />
          ))}
        </section>
      ))}
    </div>
  );
}

function Empty({ children }: { children: string }) {
  return <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">{children}</div>;
}

function ActionResult({
  entry,
  imageId,
  imageKind,
  canRun,
  env,
  me,
}: {
  entry: Entry;
  imageId: string;
  imageKind?: ImageKind;
  canRun: boolean;
  env: OutputEnv;
  me: string | null;
}) {
  const { action, latest } = entry;
  const [dialog, setDialog] = useState(false);
  const start = useStartRun(imageId);
  const cancel = useCancelRun(imageId);
  const active = !!latest && isActive(latest);
  const runnable = canRun && !!action && isEnabled(action) && runsOnDemand(action) && runsOnKind(imageKind);

  const submit = (req: StartRunRequest) =>
    start.mutate(req, {
      onSuccess: () => setDialog(false),
    });

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3" data-action={entry.id}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
          <span className="min-w-0">{entry.name}</span>
          {entry.ai ? <AiBadge /> : null}
        </h3>
        <div className="flex shrink-0 gap-1.5">
          {active && canRun ? (
            <Button size="sm" variant="outline" disabled={cancel.isPending} onClick={() => cancel.mutate(latest!.id)}>
              Cancel
            </Button>
          ) : null}
          {!active && runnable ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                start.reset();
                setDialog(true);
              }}
            >
              {latest ? 'Run again' : 'Run'}
            </Button>
          ) : null}
        </div>
      </div>

      {latest ? <RunStatus run={latest} /> : <p className="text-sm text-muted-foreground">Not run on this image yet.</p>}
      {cancel.error ? (
        <p role="alert" className="text-xs text-destructive">
          {(cancel.error as Error).message}
        </p>
      ) : null}

      {latest && runState(latest) === 'done'
        ? outputSections(latest.outputs).map(({ type, outputs, Renderer }) => (
            <Renderer key={type} run={latest} outputs={outputs} env={env} declared={action?.outputs} />
          ))
        : null}

      {latest ? <Provenance run={latest} action={action} me={me} /> : null}

      {action && runnable ? (
        <RunDialog
          action={action}
          open={dialog}
          onOpenChange={setDialog}
          engine={env.engine}
          onSubmit={submit}
          pending={start.isPending}
          error={start.error ? (start.error as Error).message : null}
        />
      ) : null}
    </div>
  );
}

/** The person-facing state: Provisioning, Running (with a percentage when reported), Done, Failed (with why). */
export function RunStatus({ run }: { run: Run }) {
  const state = runState(run);
  if (!state) return null;
  const label = stateLabel(run);
  if (state === 'failed') {
    return (
      <div className="flex items-start gap-2 text-sm text-destructive" data-state={state}>
        <CircleX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <span className="font-medium">{label}</span>
          {run.message ? <p className="break-words text-destructive/90">{run.message}</p> : null}
        </div>
      </div>
    );
  }
  if (state === 'done') {
    return (
      <div className="text-xs font-medium text-muted-foreground" data-state={state}>
        {label}
      </div>
    );
  }
  const pct = state === 'running' && typeof run.progress === 'number' ? Math.max(0, Math.min(1, run.progress)) : null;
  return (
    <div className="flex flex-col gap-1.5" data-state={state}>
      <div className="flex items-center gap-2 text-sm text-foreground">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        <span>{label}</span>
      </div>
      {pct !== null ? (
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className={cn('h-full rounded-full bg-primary transition-[width]')} style={{ width: `${pct * 100}%` }} />
        </div>
      ) : null}
    </div>
  );
}

/** Under every result: the action and version that made it, its permitted use, and who ran it, when. */
function Provenance({ run, action, me }: { run: Run; action: Action | null; me: string | null }) {
  const use = permittedUseLabel(action?.permittedUses);
  return (
    <div className="flex flex-col gap-0.5 border-t pt-2 text-xs text-muted-foreground">
      <span>
        {run.action.name} {run.action.version}
        {use ? ` · ${use}` : ''}
      </span>
      <span>{provenanceLine(run, me)}</span>
    </div>
  );
}
